import React, { useEffect, useMemo, useState } from "react";
import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";
import { useTable, useFilters } from "react-table";

import { CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="Filtrar."
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
    />
  );
}

function toISO(d) {
  if (!d) return "";
  return String(d).slice(0, 10);
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function numFmt(v) {
  const n = safeNum(v);
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function estadoInfo(row) {
  if (row.atingido) return { label: "Atingido", color: "#4caf50" };
  if (safeNum(row.qtd) > 0 || safeNum(row.clientes) > 0) {
    return { label: "Em progresso", color: "#f9a825" };
  }
  return { label: "Sem vendas", color: "#9e9e9e" };
}

export default function IncentivosVendedorDashboard({
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
  onEmptyChange,
}) {
  const { userData } = useAuth();
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [carregado, setCarregado] = useState(false);

  const canSee = !!userData?.id && !!userData?.refvendedor;

  const carregar = async () => {
    if (!canSee) {
      setRows([]);
      setCarregado(true);

      onEmptyChange?.(true);
      onLoadedChange?.(true);
      onLoadingChange?.(false);
      return;
    }

    setLoading(true);
    onLoadingChange?.(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelIncentivosResultadosVendedores", {
          idincentivo: 0,
          idutilizador: userData?.id || 0,
          funcoes: userData?.funcoes || [],
          apenasMeuVendedor: true,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const lista = Array.isArray(payload) ? payload : [];

      setRows(lista);
      setCarregado(true);

      onEmptyChange?.(lista.length === 0);
      onLoadedChange?.(true);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    if (!autoLoad && refreshToken <= 0) return;

    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, refreshToken, canSee, userData?.id, userData?.refvendedor]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "estado_visual",
        disableFilters: true,
        Cell: ({ row }) => {
          const e = estadoInfo(row.original);
          return (
            <span
              title={e.label}
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: e.color,
              }}
            />
          );
        },
      },
      { Header: "Incentivo", accessor: "incentivo", Filter: DefaultColumnFilter },
      { Header: "Grupo", accessor: "grupo", Filter: DefaultColumnFilter },
      { Header: "Patamar", accessor: "patamar", Filter: DefaultColumnFilter },
      {
        Header: "Início",
        accessor: "dataini",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => toISO(value),
      },
      {
        Header: "Fim",
        accessor: "datafim",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => toISO(value),
      },
      { Header: "Condições", accessor: "condicoes", Filter: DefaultColumnFilter },
      {
        Header: "Prémio",
        accessor: "beneficio_descricao",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Faturação",
        accessor: "obtido",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Prémio alcançado",
        accessor: "beneficio_obtido",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => (value ? numFmt(value) : ""),
      },
      {
        Header: "Estado",
        accessor: "atingido",
        Filter: DefaultColumnFilter,
        Cell: ({ row }) => estadoInfo(row.original).label,
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
  const table = useTable({ columns, data: rows, defaultColumn }, useFilters);

  const { getTableProps, getTableBodyProps, headerGroups, rows: tableRows, prepareRow } = table;

  if (!canSee) return null;

  return (
    <MDBox sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      {loading && carregado ? (
        <MDBox py={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </MDBox>
      ) : null}

      <MDBox
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: carregado && tableRows.length > 0 ? "auto" : "visible",
          maxHeight: carregado && tableRows.length > 0 ? 320 : "none",
          borderRadius: 1,
          "& tbody tr:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
        }}
      >
        <table
          {...getTableProps()}
          style={{
            width: "100%",
            minWidth: 980,
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps()}
                    key={col.id}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: "#fff",
                      textAlign: "left",
                      padding: "6px 8px",
                      fontSize: 12,
                      color: "#7b809a",
                      verticalAlign: "bottom",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div>{col.render("Header")}</div>
                    {!col.disableFilters && (
                      <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                        {col.canFilter ? col.render("Filter") : null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {tableRows.map((r) => {
              prepareRow(r);
              return (
                <tr
                  {...r.getRowProps()}
                  key={r.id}
                  style={{
                    borderBottom: "1px solid #f0f0f0",
                    transition: "background-color 0.15s",
                  }}
                >
                  {r.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{
                        padding: "6px 8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}

            {!tableRows.length && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 12, color: "#9e9e9e" }}>
                  Sem incentivos para apresentar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </MDBox>
    </MDBox>
  );
}
